import * as React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  InputAdornment,
  IconButton,
  Divider,
  Card,
  CardContent,
  Link,
  Container,
  Grid,
  Paper,
  Alert,
  FormControlLabel,
  Checkbox
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Person,
  Business,
  Info,
  Lock,
  WhatsApp
} from "@mui/icons-material";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createEmptyInstructor, Instructor, instructorToSignUpPayload } from "../../models/instructor";
import { createHubPayload, newHub, Hub } from "../../models/hub";

import { useSnackbar } from "../../components/snackbar";

const nameRegex = /^[a-zA-Z\s'-]+$/;

const isPasswordValid = (password: string) => {
  return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d\S]{8,}$/.test(password);
};

const API_URL = process.env.REACT_APP_API_URL;

function SignUp() {
    const [instructor, setInstructor] = useState<Instructor>(createEmptyInstructor());
    const [hub, setHub] = useState<Hub>(newHub());
    const [states, setStates] = useState<any[]>([]);
    const [lgas, setLgas] = useState<any[]>([]);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordRepeat, setShowPasswordRepeat] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [marketingOptIn, setMarketingOptIn] = useState(false);
    const [searchParams] = useSearchParams();

    const [referralCode, setReferralCode] = useState<string | null>(null);
    
    // const [accessCode, setAccessCode] = useState("");
    // const [hasValidAccessCode, setHasValidAccessCode] = useState(false);
    // const [isVerifyingCode, setIsVerifyingCode] = useState(false);
    
    const [units, setUnits] = useState([
        { id: 1, name: 'Individual' },
        { id: 2, name: 'Organization' }
    ]);
    const [niches, setNiches] = useState([
        { id: 1, name: 'General' },
        { id: 2, name: 'Nutrition' },
        { id: 3, name: 'Health & Wellness' },
        { id: 4, name: 'Technology & Programming' },
        { id: 5, name: 'Business & Entrepreneurship' },
        { id: 6, name: 'Academic Support (e.g. WAEC, JAMB)' },
        { id: 7, name: 'Creative Arts & Design' },
        { id: 8, name: 'Languages & Communication' },
        { id: 9, name: 'Religious & Spiritual Education' },
        { id: 10, name: 'Other' }
    ]);

    const navigate = useNavigate();
    const { showMessage } = useSnackbar();
    
    useEffect(() => {
        const ref = searchParams.get("ref");
        if (ref) {
            setReferralCode(ref);
        }
    }, []);

    useEffect(() => {
        fetch("https://vapi.verifyme.ng/v1/countries/1/states")
        .then((response) => response.json())
        .then((data) => {
            if (data.status === "success") {
                setStates(data.data);
            }
        });
    }, []);

    useEffect(() => {
        if (hub.state !== 0) {
            fetch(`https://vapi.verifyme.ng/v1/states/${hub.state}/lgas`)
                .then((response) => response.json())
                .then((data) => {
                    if (data.status === "success") {
                        setLgas(data.data);
                    }
                });
        }
    }, [hub.state]);

    const handleInstructorChange = <K extends keyof Instructor>(field: K, value: Instructor[K]) => {
        setInstructor((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const handleHubChange = (field: keyof Hub, value: any) => {
        setHub((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    // const verifyAccessCode = async () => {
    //     if (!accessCode.trim()) {
    //         setErrors(prev => ({ ...prev, accessCode: "Please enter an access code" }));
    //         return;
    //     }

    //     setIsVerifyingCode(true);
    //     try {
    //         const response = await axios.post(`${API_URL}/auth/instructors/verify-access-code`, {
    //             access_code: accessCode.trim()
    //         });
            
    //         if (response.data) {
    //             setHasValidAccessCode(true);
    //             setErrors(prev => ({ ...prev, accessCode: "" }));
    //             showMessage('Access code verified successfully!', 4);
    //         } else {
    //             setErrors(prev => ({ ...prev, accessCode: "Invalid access code. Please try again." }));
    //         }
    //     } catch (error: any) {
    //         setErrors(prev => ({ ...prev, accessCode: "Invalid access code. Please try again." }));
    //     } finally {
    //         setIsVerifyingCode(false);
    //     }
    // };

    const handleWhatsAppRedirect = () => {
        const message = encodeURIComponent("Hi! I would like to request an access code for ELMS Instructor registration.");
        const whatsappUrl = `https://wa.me/23407059148543?text=${message}`;
        window.open(whatsappUrl, '_blank');
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        // Personal Information Validation
        if (!instructor.name.trim()) {
            newErrors.name = "Full name is required";
        } else if (!nameRegex.test(instructor.name)) {
            newErrors.name = "Name can only contain letters, spaces, hyphens, or apostrophes";
        }

        if (!instructor.email.trim()) {
            newErrors.email = "Email address is required";
        }
        
        if (!instructor.phone_number.trim()) {
            newErrors.phone_number = "Phone number is required";
        } 

        if (!instructor.password.trim()) {
            newErrors.password = "Password is required";
        } else if (!isPasswordValid(instructor.password)) {
            newErrors.password = "Password must be at least 8 characters with letters and numbers";
        }
        
        if (!instructor.password_repeat.trim()) {
            newErrors.password_repeat = "Please confirm your password";
        } else if (instructor.password_repeat !== instructor.password) {
            newErrors.password_repeat = "Passwords do not match";
        }

        // Hub Information Validation
        if (!hub.name.trim()) {
            newErrors.hub_name = 'Hub name is required';
        }
        if (hub.type === 0) {
            newErrors.hub_type = "Please select your hub type";
        }
        if (hub.niche === 0) {
            newErrors.hub_niche = "Please select your hub niche";
        }
        if (hub.state === 0) {
            newErrors.hub_state = "Please select your state";
        }
        if (hub.lga === 0) {
            newErrors.hub_lga = "Please select your LGA";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            showMessage('Please fill in all required fields correctly', 1);
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await axios.post(`${API_URL}/auth/instructors/sign-up`, 
                {
                    instructor: instructorToSignUpPayload(instructor), 
                    hub: createHubPayload(hub),
                    referral_code: referralCode
                }
            );
            navigate(`/verify/sign-up?instructorEmail=${instructor.email}`);
        } catch (error: any) {
            showMessage(error.response?.data?.detail || 'An error occurred during registration', 0);
        } finally {
            setIsSubmitting(false);
        }
    };

    const fieldStyles = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'white',
            borderRadius: 3,
            '&:hover fieldset': {
                borderColor: '#667eea'
            },
            '&.Mui-focused fieldset': {
                borderColor: '#667eea'
            }
        }
    };

    return (
        <Box sx={{ minHeight: "100vh", display: "flex" }}>
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: "grey.100",
                }}
            >
                <Box
                    sx={{
                        p: 3,
                        display: "flex",
                        alignItems: "center"
                    }}
                >
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: "1.5rem",
                            userSelect: "none",
                            fontFamily: "'Inter', 'Roboto', sans-serif",
                            color: "#3B82F6",
                            letterSpacing: "-0.02em"
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                color: "green",
                                fontWeight: 800,
                                fontSize: "1.6rem"
                            }}
                        >
                            e
                        </Box>
                        LMS
                        <Box
                            component="span"
                            sx={{
                                color: "green",
                                fontWeight: 600,
                                fontSize: "0.9rem",
                                ml: 1,
                                opacity: 0.7
                            }}
                        >
                            Instructor
                        </Box>
                    </Typography>
                </Box>

                {/* Form Container */}
                <Container
                    maxWidth="lg"
                    sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                    }}
                >
                    <Box sx={{ maxWidth: 800, mx: "auto", width: "100%" }}>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 700,
                                mb: 1,
                                color: "#1a1a1a"
                            }}
                        >
                            Create Account
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                mb: 4,
                                color: "text.secondary"
                            }}
                        >
                            Set up your teaching hub and start educating students today.
                        </Typography>

                        <Box
                            sx={{
                                mb: 4,
                                p: 2,
                                borderRadius: 2,
                                backgroundColor: "#ecfdf5",
                                border: "1px solid #a7f3d0",
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "0.95rem",
                                    fontWeight: 600,
                                    color: "#065f46"
                                }}
                            >
                                🎉 First Month Free Onboarding
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: "0.85rem",
                                    color: "#065f46",
                                    opacity: 0.9
                                }}
                            >
                                All new instructors get 1 month free access to explore ELMS and learn how the platform works.
                                If you need help, you can reach us anytime on WhatsApp after registration.
                            </Typography>

                            <Button
                                size="small"
                                href="https://wa.me/2347059148543"
                                target="_blank"
                                sx={{
                                    alignSelf: "flex-start",
                                    mt: 1,
                                    textTransform: "none",
                                    fontWeight: 600,
                                    color: "#065f46"
                                }}
                            >
                                Contact Support on WhatsApp
                            </Button>
                        </Box>

                        <Stack spacing={4}>
                            {/* <Paper 
                                elevation={0}
                                sx={{ 
                                    p: 3, 
                                    borderRadius: 3,
                                    backgroundColor: 'white',
                                    border: '1px solid',
                                    borderColor: hasValidAccessCode ? '#4caf50' : 'grey.200'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Lock sx={{ mr: 2, color: hasValidAccessCode ? '#4caf50' : '#667eea' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Access Verification
                                    </Typography>
                                </Box>

                                {!hasValidAccessCode ? (
                                    <Stack spacing={3}>
                                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                                            ELMS restricts sign up for now to the general public to ensure it only gets to the right users, but you can quickly request an access code (free) and gain access after a few questions.
                                        </Alert>

                                        <Grid container spacing={2}>
                                            <Grid size={{ xs: 12, md: 8 }}>
                                                <TextField
                                                    fullWidth
                                                    label="Access Code"
                                                    variant="outlined"
                                                    value={accessCode}
                                                    onChange={(e) => setAccessCode(e.target.value)}
                                                    error={!!errors.accessCode}
                                                    helperText={errors.accessCode}
                                                    placeholder="Enter your access code"
                                                    sx={fieldStyles}
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') {
                                                            verifyAccessCode();
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 4 }}>
                                                <Button
                                                    fullWidth
                                                    variant="outlined"
                                                    onClick={verifyAccessCode}
                                                    disabled={isVerifyingCode || !accessCode.trim()}
                                                    sx={{ 
                                                        height: '56px',
                                                        borderRadius: 3,
                                                        borderColor: '#667eea',
                                                        color: '#667eea',
                                                        '&:hover': {
                                                            borderColor: '#667eea',
                                                            backgroundColor: '#f5f7ff'
                                                        }
                                                    }}
                                                >
                                                    {isVerifyingCode ? 'Verifying...' : 'Verify Code'}
                                                </Button>
                                            </Grid>
                                        </Grid>

                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                Don't have an access code yet?
                                            </Typography>
                                            
                                            <Button
                                                variant="contained"
                                                startIcon={<WhatsApp />}
                                                onClick={handleWhatsAppRedirect}
                                                sx={{
                                                    backgroundColor: '#25D366',
                                                    color: 'white',
                                                    borderRadius: 3,
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    px: 3,
                                                    py: 1.5,
                                                    '&:hover': {
                                                        backgroundColor: '#1ea952'
                                                    }
                                                }}
                                            >
                                                Request Access Code via WhatsApp
                                            </Button>
                                            
                                            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                                                Contact: +234 070 5914 8543
                                            </Typography>
                                        </Box>
                                    </Stack>
                                ) : (
                                    <Alert severity="success" sx={{ borderRadius: 2 }}>
                                        ✓ Access code verified! You can now proceed with registration.
                                    </Alert>
                                )}
                            </Paper> */}

                            {/* Personal Information Section */}
                            <Paper 
                                elevation={0}
                                sx={{ 
                                    p: 3, 
                                    borderRadius: 3,
                                    backgroundColor: 'white',
                                    border: '1px solid',
                                    borderColor: 'grey.200',
                                    // opacity: hasValidAccessCode ? 1 : 0.5,
                                    // pointerEvents: hasValidAccessCode ? 'auto' : 'none'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Person sx={{ mr: 2, color: '#667eea' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Personal Information
                                    </Typography>
                                </Box>
                                
                                <Stack spacing={3}>
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <TextField
                                                fullWidth
                                                label="Full Name"
                                                variant="outlined"
                                                value={instructor.name}
                                                onChange={(e) => handleInstructorChange("name", e.target.value)}
                                                error={!!errors.name}
                                                helperText={errors.name}
                                                placeholder="Enter your full name"
                                                sx={fieldStyles}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <TextField
                                                fullWidth
                                                label="Email Address"
                                                variant="outlined"
                                                type="email"
                                                value={instructor.email}
                                                onChange={(e) => handleInstructorChange("email", e.target.value)}
                                                error={!!errors.email}
                                                helperText={errors.email}
                                                placeholder="Enter your email address"
                                                sx={fieldStyles}
                                            />
                                        </Grid>
                                    </Grid>

                                    <TextField
                                        fullWidth
                                        label="Phone Number"
                                        variant="outlined"
                                        value={instructor.phone_number}
                                        onChange={(e) => handleInstructorChange("phone_number", e.target.value)}
                                        error={!!errors.phone_number}
                                        helperText={errors.phone_number}
                                        placeholder="Enter your phone number"
                                        sx={fieldStyles}
                                    />

                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <TextField
                                                fullWidth
                                                label="Password"
                                                variant="outlined"
                                                type={showPassword ? "text" : "password"}
                                                value={instructor.password}
                                                onChange={(e) => handleInstructorChange("password", e.target.value)}
                                                error={!!errors.password}
                                                helperText={errors.password || "Password must contain at least 8 characters with letters and numbers"}
                                                placeholder="Create a password"
                                                sx={fieldStyles}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() => setShowPassword(!showPassword)}
                                                                edge="end"
                                                            >
                                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <TextField
                                                fullWidth
                                                label="Confirm Password"
                                                variant="outlined"
                                                type={showPasswordRepeat ? "text" : "password"}
                                                value={instructor.password_repeat}
                                                onChange={(e) => handleInstructorChange("password_repeat", e.target.value)}
                                                error={!!errors.password_repeat}
                                                helperText={errors.password_repeat}
                                                placeholder="Confirm your password"
                                                sx={fieldStyles}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() => setShowPasswordRepeat(!showPasswordRepeat)}
                                                                edge="end"
                                                            >
                                                                {showPasswordRepeat ? <VisibilityOff /> : <Visibility />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Stack>
                            </Paper>

                            {/* Hub Information Section */}
                            <Paper 
                                elevation={0}
                                sx={{ 
                                    p: 3, 
                                    borderRadius: 3,
                                    backgroundColor: 'white',
                                    border: '1px solid',
                                    borderColor: 'grey.200',
                                    // opacity: hasValidAccessCode ? 1 : 0.5,
                                    // pointerEvents: hasValidAccessCode ? 'auto' : 'none'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Business sx={{ mr: 2, color: '#667eea' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Hub Information
                                    </Typography>
                                </Box>

                                <Stack spacing={3}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            backgroundColor: '#f8f9ff',
                                            borderRadius: 2,
                                            border: '1px solid #e3f2fd',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <Info sx={{ mr: 2, color: '#667eea', fontSize: 20 }} />
                                        <Typography variant="body2" color="text.secondary">
                                            Think of the Hub Name as your personal academy or teaching brand.
                                        </Typography>
                                    </Box>

                                    <TextField
                                        fullWidth
                                        label="Hub Name"
                                        variant="outlined"
                                        value={hub.name}
                                        onChange={(e) => handleHubChange("name", e.target.value)}
                                        error={!!errors.hub_name}
                                        helperText={errors.hub_name}
                                        placeholder="e.g., Smith Academy, Tech Learning Hub"
                                        sx={fieldStyles}
                                    />
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <FormControl fullWidth error={!!errors.hub_type}>
                                                <InputLabel>Hub Type</InputLabel>
                                                <Select
                                                    value={hub.type || ""}
                                                    onChange={(e) => handleHubChange("type", Number(e.target.value))}
                                                    label="Hub Type"
                                                    sx={{
                                                        backgroundColor: 'white',
                                                        borderRadius: 3,
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: '#667eea'
                                                        },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: '#667eea'
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value={0}><em>Select Hub Type</em></MenuItem>
                                                    {units.map((unit) => (
                                                        <MenuItem key={unit.id} value={unit.id}>
                                                            {unit.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {errors.hub_type && (
                                                    <Typography color="error" variant="caption" sx={{ ml: 2, mt: 0.5 }}>
                                                        {errors.hub_type}
                                                    </Typography>
                                                )}
                                            </FormControl>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <FormControl fullWidth error={!!errors.hub_niche}>
                                                <InputLabel>Hub Specialization</InputLabel>
                                                <Select
                                                    value={hub.niche || ""}
                                                    onChange={(e) => handleHubChange("niche", Number(e.target.value))}
                                                    label="Hub Specialization"
                                                    sx={{
                                                        backgroundColor: 'white',
                                                        borderRadius: 3,
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: '#667eea'
                                                        },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: '#667eea'
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value={0}><em>Select Specialization</em></MenuItem>
                                                    {niches.map((niche) => (
                                                        <MenuItem key={niche.id} value={niche.id}>
                                                            {niche.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {errors.hub_niche && (
                                                    <Typography color="error" variant="caption" sx={{ ml: 2, mt: 0.5 }}>
                                                        {errors.hub_niche}
                                                    </Typography>
                                                )}
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, md: 4 }}>
                                            <TextField
                                                fullWidth
                                                label="Country"
                                                variant="outlined"
                                                value={hub.country}
                                                disabled
                                                sx={fieldStyles}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 4 }}>
                                            <FormControl fullWidth error={!!errors.hub_state}>
                                                <InputLabel>State</InputLabel>
                                                <Select
                                                    value={hub.state || ""}
                                                    onChange={(e) => {
                                                        const stateId = Number(e.target.value);
                                                        handleHubChange("state", stateId);
                                                        handleHubChange("lga", 0);
                                                    }}
                                                    label="State"
                                                    sx={{
                                                        backgroundColor: 'white',
                                                        borderRadius: 3,
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: '#667eea'
                                                        },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: '#667eea'
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value={0}><em>Select State</em></MenuItem>
                                                    {states.map((state) => (
                                                        <MenuItem key={state.id} value={state.id}>
                                                            {state.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {errors.hub_state && (
                                                    <Typography color="error" variant="caption" sx={{ ml: 2, mt: 0.5 }}>
                                                        {errors.hub_state}
                                                    </Typography>
                                                )}
                                            </FormControl>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 4 }}>
                                            <FormControl fullWidth disabled={hub.state === 0} error={!!errors.hub_lga}>
                                                <InputLabel>Local Government Area (LGA)</InputLabel>
                                                <Select
                                                    value={hub.lga || ""}
                                                    onChange={(e) => handleHubChange("lga", Number(e.target.value))}
                                                    label="Local Government Area (LGA)"
                                                    sx={{
                                                        backgroundColor: 'white',
                                                        borderRadius: 3,
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: '#667eea'
                                                        },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: '#667eea'
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value={0}><em>Select LGA</em></MenuItem>
                                                    {hub.state !== 0 &&
                                                        lgas.map((lga) => (
                                                            <MenuItem key={lga.id} value={lga.id}>
                                                                {lga.name}
                                                            </MenuItem>
                                                        ))}
                                                </Select>
                                                {errors.hub_lga && (
                                                    <Typography color="error" variant="caption" sx={{ ml: 2, mt: 0.5 }}>
                                                        {errors.hub_lga}
                                                    </Typography>
                                                )}
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Stack>
                            </Paper>

                            {/* Marketing Opt-in */}
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={marketingOptIn}
                                        onChange={(e) => setMarketingOptIn(e.target.checked)}
                                        sx={{ color: '#667eea', '&.Mui-checked': { color: '#667eea' } }}
                                    />
                                }
                                label={
                                    <Typography variant="body2" color="text.secondary">
                                        Send me updates, offers, and product news (optional)
                                    </Typography>
                                }
                            />

                            {/* Submit Button */}
                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                sx={{
                                    py: 1.5,
                                    borderRadius: 3,
                                    backgroundColor: '#3B82F6',
                                    boxShadow: "0 4px 20px rgba(102, 126, 234, 0.4)",
                                    fontSize: "1rem",
                                    fontWeight: 600,
                                    textTransform: "none",
                                    mt: 2,
                                    '&:hover': {
                                        boxShadow: "0 6px 25px rgba(102, 126, 234, 0.6)",
                                        transform: "translateY(-1px)"
                                    },
                                    '&:disabled': {
                                        opacity: 0.7,
                                        transform: "none"
                                    },
                                    transition: "all 0.3s ease"
                                }}
                            >
                                {isSubmitting ? "Creating Account..." : "Create Instructor Account"}
                            </Button>

                            {/* Sign In Link + Terms */}
                            <Stack spacing={0.5} alignItems="center">
                                <Typography variant="body2" color="text.secondary" textAlign="center">
                                    Already have an account?{' '}
                                    <Link
                                        component="button"
                                        onClick={() => navigate('/sign-in')}
                                        sx={{
                                            color: '#667eea',
                                            fontWeight: 600,
                                            textDecoration: 'none',
                                            '&:hover': { textDecoration: 'underline' }
                                        }}
                                    >
                                        Sign in here
                                    </Link>
                                </Typography>
                                <Typography variant="caption" color="text.disabled" textAlign="center">
                                    By signing up, you agree to our{' '}
                                    <Link href="https://elms.ng/terms-of-use/" target="_blank" rel="noopener noreferrer" sx={{ color: 'text.disabled' }}>
                                        Terms of Use
                                    </Link>
                                    {' '}and{' '}
                                    <Link href="https://elms.ng/privacy-policy/" target="_blank" rel="noopener noreferrer" sx={{ color: 'text.disabled' }}>
                                        Privacy Policy
                                    </Link>
                                </Typography>
                            </Stack>
                        </Stack>
                    </Box>
                </Container>

                {/* Footer Links */}
                {/* <Box sx={{ textAlign: 'center', mt: 3, paddingBottom: 4, maxWidth: 700, mx: 'auto' }}>
                    <Stack
                        direction="row"
                        spacing={2}
                        divider={<Divider orientation="vertical" sx={{ backgroundColor: "grey.300"}} flexItem />}
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Link
                            href="https://elms.ng/terms-of-use/"
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                                textDecoration: 'underline',
                                fontSize: '0.875rem',
                            }}
                            >
                            Terms of Use
                        </Link>

                        <Link
                            href="https://elms.ng/privacy-policy/"
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                                textDecoration: 'underline',
                                fontSize: '0.875rem',
                            }}
                        >
                        Privacy Policy
                    </Link>

                    </Stack>
                </Box> */}
            </Box>
        </Box>
    );
}

export default SignUp;